/**
 * What the signed-in user is allowed to do.
 *
 * The UI needs this to hide controls that would 403. The alternative is a copy
 * of the policy in JavaScript, which drifts the first time somebody changes a
 * default on the server and forgets this file, and the failure is a button that
 * looks available and is not.
 *
 * So the server answers instead. `/rbac/me` returns the caller's own permission
 * keys, already resolved against their organization's overrides.
 *
 * **Hiding a control is a courtesy, not a control.** Every one of these is
 * backed by a server-side check that returns 403 regardless of what rendered.
 */
import { getHeaders } from 'auth/auth';
import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../baseQuery';

export const rbacAPI = createApi({
  reducerPath: 'rbacAPI',
  baseQuery,
  // Permissions change when an admin edits them, which is rare and never in
  // this user's own session. Long-lived, and refetched on mount so a change
  // lands on the next page load rather than needing a sign-out.
  keepUnusedDataFor: 300,
  tagTypes: ['Permissions', 'Matrix'],
  endpoints: (builder) => ({
    getMyPermissions: builder.query({
      query: () => ({
        url: '/b2b/rbac/me',
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? response,
      providesTags: ['Permissions'],
    }),

    /**
     * Every permission that exists, with labels and section grouping.
     *
     * The admin screen renders itself from this rather than listing permissions
     * here. Add one to the server registry and it becomes editable without a
     * frontend release; keep a copy here and the two drift silently.
     */
    getRbacCatalogue: builder.query({
      query: () => ({
        url: '/b2b/rbac/catalogue',
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? response,
    }),

    /** The effective policy for the caller's own organization. */
    getRbacMatrix: builder.query({
      query: () => ({
        url: '/b2b/rbac/matrix',
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? response,
      providesTags: ['Matrix'],
    }),

    updateRbacMatrix: builder.mutation({
      query: (body) => ({
        url: '/b2b/rbac/matrix',
        method: 'PUT',
        headers: getHeaders(),
        body,
      }),
      transformResponse: (response) => response?.data ?? response,
      // `Permissions` as well as `Matrix`: an admin who just removed a
      // permission from their own role should see the affected controls
      // disappear, not on the next sign-in.
      invalidatesTags: ['Matrix', 'Permissions'],
    }),
  }),
});

export const {
  useGetMyPermissionsQuery,
  useGetRbacCatalogueQuery,
  useGetRbacMatrixQuery,
  useUpdateRbacMatrixMutation,
} = rbacAPI;
